/**
 * Identity-only mode's shared vocabulary — the error, the exemptions, the
 * capability names and the customer-facing sentence.
 *
 * The RULE is `tenant-mode.ts` beside this (pure, two layers). The STATE is
 * resolved by `resolveEffectiveTenantMode` in `@papan/database` (it needs the
 * tenant row and the singleton). The GATES are `assertTenantWritable` /
 * `assertTenantWorkspaceReadable` there too. This module is what all three, plus
 * the API middleware, plus the SPA, have to agree on — so it lives in the
 * dependency-free package.
 *
 * Design of record: docs/plans/2026-08-11_IDENTITY_ONLY_TENANT_MODE.md §5.3.
 * Same shape as `write-freeze.ts`, deliberately: that feature is the closest
 * precedent and copying its structure makes the differences legible.
 */

/**
 * What was being attempted when the mode refused it.
 *
 * This rides the 403 body so the SPA can explain the refusal in the user's own
 * terms rather than printing one sentence for every denial. It is **diagnostic
 * vocabulary, not a permission lattice** — nothing grants or checks these, and
 * adding one confers nothing. The permission system is unchanged and still runs;
 * mode supplements it (§5.1).
 *
 * Keep them coarse. A capability per route would be a second authorization
 * model with none of the first one's guarantees.
 */
export type TenantModeCapability =
  /** Create, edit, move, rename, copy, delete, restore or purge in the tenant's drive. */
  | 'storage.write'
  /** Direct drive navigation, listing, search, bin or quarantine reads. */
  | 'storage.read'
  /** Agent workspace, shell or session creation in the tenant. */
  | 'workspace.create'
  /** Personal share links and the keybook. */
  | 'share.write'
  /** A mini-app's own database, when the app is owned by this tenant. */
  | 'appdb.write'
  /** Acquiring or changing what this tenant may spend — subscriptions, credit
   *  purchases, promo redemption. */
  | 'billing.write'

/*
 * ── Open interaction: `who_pays` and the actor-pays default (issue #369, A3) ─
 *
 * Not a capability, and deliberately not one — it is a decision this table will
 * have to serve once mini-app manifests can declare a payer.
 *
 * `resolveAppPayer` (features/agent/api/app-payer.ts) resolves `'user'`, an
 * absent field, and an UNFUNDED `'owner'` declaration all to **actor pays**. If
 * the actor's personal tenant is identity-only, that names a payer which — with
 * `billing.write` gated above — can never acquire credit. The path is
 * unreachable today because `who_pays` is not exposed through the SDK; the two
 * candidate resolutions (fail closed carrying `IDENTITY_ONLY_CODE`, or force
 * owner-pays) are written out at that call site.
 *
 * Whoever exposes `who_pays` decides this. Adding a capability member is NOT the
 * fix on its own: these names confer nothing, so a new one would describe the
 * refusal without causing it.
 */

/**
 * Why an operation is allowed to proceed in an identity-only tenant.
 *
 * ── Fail-closed by construction ─────────────────────────────────────────
 *
 * There is no "unknown origin" member and no default. An operation with no
 * exemption is REFUSED, so a path added later is gated automatically and its
 * author has to decide. §6.1 states this as a requirement and names what it is
 * ruling out: **no general `skipModeCheck` boolean.** A boolean is a request to
 * be trusted; a reason is a claim that can be read, audited and — in one case
 * below — verified.
 */
export type TenantModeExemption =
  /**
   * Structural drive scaffolding minted by the platform, not content the person
   * chose to add — the personal root at signup, the `Shared`/`Library`/`Projects`
   * roots, a member's folder on invite accept.
   *
   * This one is REQUIRED for reversibility, which is the whole product promise
   * (§9): a personal root is created before any mode can be set, and an
   * identity-only tenant still needs its root to exist or switching back to
   * `full` would have nothing to reveal. Refusing here would mean the switch
   * back needs a repair pass, which §9 explicitly forbids.
   */
  | { reason: 'provisioning' }
  /**
   * A retention, GC or reconciliation sweep run by the worker against every
   * tenant — bin retention, content-blob collection, orphan reconciliation.
   *
   * Not a convenience. A sweep that stopped running against identity-only
   * tenants would leak storage indefinitely on exactly the tenants an operator
   * has decided are not workspaces, and the leak would be invisible because
   * nobody is looking at a drive nobody can open.
   *
   * `actor` must be a non-user identity (§6.1). A sweep is the platform acting
   * on its own behalf; if a request can reach this reason, it is not a sweep.
   */
  | { reason: 'maintenance'; actor: 'worker' | 'migration' | 'platform-admin' }
  /**
   * An inbound cross-tenant share landing whose bytes never enter this tenant —
   * the `.shared` row and the roots that hold it.
   *
   * Distinct from the write freeze's version of this exemption in ONE way that
   * matters: the freeze allowed it because refusing punished the wrong customer
   * (the paid-up sharer). Here the reason is narrower — a `.shared` is a
   * cross-tenant REFERENCE, so permitting it does not make an identity-only
   * tenant into a storage destination, which is the thing the mode exists to
   * prevent. A cross-tenant COPY writes real bytes and is refused.
   */
  | { reason: 'inbound-share' }
  /*
   * ── `account-config` was the fourth, and it is GONE (issue #369) ────────
   *
   * It existed because a person's own configuration — theme, editor
   * preferences, sql-history — was STORED as JSON files in their personal
   * tenant's `_system/.config/`, so an identity-only person could not save a
   * theme without an exemption. Its own docblock named the honest fix and
   * called it a debt: *"account configuration does not belong in a drive at
   * all."*
   *
   * That fix landed. Preferences moved to the `users.settings` column (the UI
   * half is `UserUiSettings` in `user-settings.ts`), sql-history moved to
   * device-local storage, and the `/api/settings/:key` route that wrote the
   * files was deleted along with this reason.
   *
   * **An identity-only tenant now needs ZERO writes into its own drive**,
   * which is what the mode always claimed. Do not reintroduce a reason of this
   * shape: a new write into a personal tenant that "is really configuration"
   * is the same debt again, and the answer is a column, not an exemption.
   */

/** Machine-readable code on the wire. The SPA branches on this, never on copy. */
export const IDENTITY_ONLY_CODE = 'identity_only_tenant'

/**
 * The customer-facing sentence.
 *
 * Says what this account still IS, not merely what it cannot do — because the
 * mode's whole point is that the person keeps their identity, their
 * organizations and every app shared with them. A refusal that reads like a
 * suspension describes the wrong product.
 *
 * English-only, and that is a constraint rather than an oversight: this renders
 * at a SERVER-side refusal, and server-rendered copy in this codebase cannot use
 * i18n (`@papan/i18n` is browser-only). A localized SPA renders its own string
 * from `IDENTITY_ONLY_CODE` rather than printing this one.
 */
export function identityOnlyMessage(): string {
  return 'This account does not have a personal Drive on this deployment. '
    + 'You can still sign in, use organizations you belong to, and open apps '
    + 'and files shared with you.'
}

/**
 * Thrown by the mode gates when an identity-only tenant is the target of an
 * operation the mode refuses.
 *
 * ── Matched by `name`, so the name is load-bearing ──────────────────────
 *
 * `apps/api/src/middleware/error.ts` maps this to **403** with
 * `IDENTITY_ONLY_CODE` by checking `err.name` — the pattern already used there
 * for `TenantWriteFrozenError`, `MaxFolderDepthError` and `BrandAssetError`,
 * specifically so the middleware need not import the throwing package. Renaming
 * this class silently turns every refusal into an opaque 500.
 *
 * 403 rather than 402: there is nothing to pay. This is a configuration an
 * operator chose, not a debt, and routing it through the SPA's money branch
 * would offer a checkout that fixes nothing.
 *
 * **Not every surface reaches that middleware.** tus owns its response pipeline,
 * and collab and the agent/mini-app sync path are WebSocket — each translates
 * this at its own boundary, reading `IDENTITY_ONLY_CODE` so the refusals cannot
 * drift apart.
 */
export class IdentityOnlyTenantError extends Error {
  readonly code = IDENTITY_ONLY_CODE
  /** What was refused, for the SPA's explanation and for logs. */
  readonly capability: TenantModeCapability
  constructor(capability: TenantModeCapability, message: string = identityOnlyMessage()) {
    super(message)
    // Set explicitly: subclassing Error does not reliably carry the constructor
    // name through transpilation, and the central handler matches on it.
    this.name = 'IdentityOnlyTenantError'
    this.capability = capability
  }
}
